import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight, ChevronLeft, Layers, Calendar, Search, User, MapPin, Globe,
  PenTool, Plug, Boxes, Star,
} from "lucide-react";
import { useShop, API } from "../context/ShopContext";
import { useLocale } from "../context/LocaleContext";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { imgSrc } from "./Portfolio";

const content = {
  it: {
    back: "Torna al portfolio",
    prev: "Precedente",
    next: "Successivo",
    askInfo: "Chiedi info",
    infoSubject: "Richiesta informazioni",
    labels: { tipologia: "Tipologia sito", data: "Data", seo: "SEO", cliente: "Cliente", sede: "Sede legale", url: "URL" },
    fallbackDesc: "Progetto realizzato da IdeaMedia: studio grafico, sviluppo e ottimizzazione su misura per il cliente, con particolare attenzione alla semplicit\u00e0 di utilizzo e alla funzionalit\u00e0.",
  },
  en: {
    back: "Back to portfolio",
    prev: "Previous",
    next: "Next",
    askInfo: "Ask for info",
    infoSubject: "Information request",
    labels: { tipologia: "Site type", data: "Date", seo: "SEO", cliente: "Client", sede: "Head office", url: "URL" },
    fallbackDesc: "A project by IdeaMedia: graphic design, development and tailor-made optimization for the client, with particular attention to ease of use and functionality.",
  },
};

const META_ORDER = [
  { key: "tipologia", icon: Layers },
  { key: "data", icon: Calendar },
  { key: "seo", icon: Search },
  { key: "cliente", icon: User },
  { key: "sede", icon: MapPin },
  { key: "url", icon: Globe },
];

const sectionIcon = (title) => {
  if (/DESIGN/i.test(title)) return PenTool;
  if (/PLUGIN/i.test(title)) return Plug;
  if (/SEO/i.test(title)) return Search;
  if (/INTEGRA/i.test(title)) return Boxes;
  return Star;
};

const PortfolioDetail = () => {
  const { slug } = useParams();
  const { lang } = useShop();
  const { lp, registerAltUrls } = useLocale();
  const navigate = useNavigate();
  const c = content[lang];
  const [item, setItem] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [itemRes, catsRes] = await Promise.all([
          axios.get(`${API}/portfolio/item/${slug}`),
          axios.get(`${API}/portfolio/categories`),
        ]);
        if (active) {
          setItem(itemRes.data);
          setCats(catsRes.data);
        }
      } catch (e) {
        if (active) navigate(lp("/portfolio"), { replace: true });
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
    return () => {
      active = false;
    };
  }, [slug, navigate]);

  // Registra gli URL alternati reali (it/en) per hreflang + language switcher.
  useEffect(() => {
    if (item) {
      registerAltUrls(`/portfolio/${item.slug}`, `/en/portfolio/${item.slug_en || item.slug}`);
    }
  }, [item, registerAltUrls]);

  if (loading || !item) {
    return (
      <div className="idm-loading idm-loading-page" data-testid="loading">
        <span className="idm-spinner" />
      </div>
    );
  }

  const catName = (s) => {
    const cat = cats.find((x) => x.slug === s);
    return cat ? cat.name[lang] : s;
  };
  const description = item.description?.[lang]?.trim() || item.description?.it?.trim() || c.fallbackDesc;
  const rawMeta = { tipologia: catName(item.category), ...(item.meta || {}) };
  const metaVal = (key) => (lang === "en" && rawMeta[`${key}_en`]) ? rawMeta[`${key}_en`] : rawMeta[key];
  const metaRows = META_ORDER.filter(({ key }) => metaVal(key));
  const sections = item.sections || [];

  return (
    <div className="idm-pfdx" data-testid="portfolio-detail-page">
      <Seo title={item.title[lang]} description={description.slice(0, 160)} image={item.image} />

      {/* Full-height dark panel - original site style */}
      <aside className="idm-pfdx-dark" data-testid="pfd-dark-panel">
        <h1 className="idm-pfdx-title" data-testid="pfd-title">{item.title[lang]}</h1>

        <ul className="idm-pfdx-meta" data-testid="pfd-meta">
          {metaRows.map(({ key, icon: Icon }) => (
            <li key={key}>
              <Icon size={15} strokeWidth={2} />
              <span className="idm-pfdx-meta-label">{c.labels[key]} :</span>
              {key === "url" ? (
                <a href={`https://${metaVal(key).replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer">
                  {metaVal(key)}
                </a>
              ) : (
                metaVal(key)
              )}
            </li>
          ))}
        </ul>

        <button className="idm-pfdx-cta" onClick={() => setModalOpen(true)} data-testid="pfd-cta-info">
          {c.askInfo}
        </button>

        <div className="idm-pfdx-nav">
          {item.prev ? (
            <Link to={lp(`/portfolio/${item.prev.slug}`, item.prev.slug_en)} className="idm-pfdx-navlink" data-testid="pfd-prev">
              <span className="idm-pfdx-circle"><ChevronLeft size={17} /></span>
              <span className="idm-pfdx-navtext">
                <em>{c.prev}</em>
                <small>{item.prev.title[lang]}</small>
              </span>
            </Link>
          ) : <span />}
          {item.next ? (
            <Link to={lp(`/portfolio/${item.next.slug}`, item.next.slug_en)} className="idm-pfdx-navlink next" data-testid="pfd-next">
              <span className="idm-pfdx-navtext">
                <em>{c.next}</em>
                <small>{item.next.title[lang]}</small>
              </span>
              <span className="idm-pfdx-circle"><ChevronRight size={17} /></span>
            </Link>
          ) : <span />}
        </div>
        <Link to={lp("/portfolio")} className="idm-pfdx-back" data-testid="pfd-back">
          <ChevronLeft size={13} /> {c.back}
        </Link>
      </aside>

      {/* Right content */}
      <div className="idm-pfdx-content">
        <div className="idm-pfdx-imgwrap" data-testid="pfd-image">
          {item.image ? (
            <img src={imgSrc(item.image)} alt={item.title[lang]} />
          ) : (
            <div className="idm-pf-noimg" style={{ minHeight: 320 }} />
          )}
        </div>

        <div className="idm-pfdx-titlebar">
          <h2>{item.title[lang]}</h2>
        </div>

        <p className="idm-pfdx-desc" data-testid="pfd-description">{description}</p>

        {sections.length > 0 && (
          <div className="idm-pfdx-sections" data-testid="pfd-sections">
            {sections.map((sec) => {
              const Icon = sectionIcon(sec.title);
              const lines = (lang === "en" && sec.lines_en?.length) ? sec.lines_en : sec.lines;
              return (
                <div className="idm-pfdx-sec" key={sec.title}>
                  <Icon size={40} strokeWidth={1.4} />
                  <h3>{sec.title}</h3>
                  {lines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ContactModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={c.askInfo}
        defaultSubject={`${c.infoSubject}: ${item.title[lang]}`}
      />
    </div>
  );
};

export default PortfolioDetail;
